import React from 'react';
import { usePagination } from '_utils/hooks';
import {
  Container,
  Prev,
  Next,
  Current,
  Page,
  Gap,
  PrevDisabled,
  NextDisabled,
  ArrowWrapper
} from './StyledPagination';
import { useLocation } from 'react-router-dom';
import { SimpleArrow } from '_utils/icons';
import ScreenReader from '_utils/components/Corporate/ScreenReader';
import { scrollTop } from '_utils/components/Corporate/Scroller';
import { useTranslation } from 'react-i18next';

type PaginationProps = {
  count: number;
  pageSize?: number;
  buffer?: number;
  pageIndex?: number;
  baseUrl: string;
  onNavigate?: (number) => void;
  ariaLabel?: string;
};

type PageLinkProps = {
  baseUrl: string;
  page: number;
};

const buildPageLink = ({ baseUrl, page }: PageLinkProps): string => baseUrl.replace(/page=\d+/, `page=${page}`)

const Pagination: React.FC<PaginationProps> = ({
  baseUrl,
  onNavigate,
  ariaLabel,
  pageIndex = undefined,
  pageSize = 20,
  buffer = 1,
  count = 0
}) => {
  const location = useLocation();
  const { search } = location || {};
  const searchParams = new URLSearchParams(search);
  const [t] = useTranslation();

  if (pageIndex === undefined) {
    pageIndex = parseInt(searchParams.get('page')) || 1;
  }

  const nextLink = buildPageLink({ baseUrl, page: pageIndex + 1 });
  const prevLink = buildPageLink({ baseUrl, page: pageIndex - 1 });

  const { prevEnabled, items, nextEnabled } = usePagination({ pageSize, pageIndex, buffer, count });

  const onNav = (e, page) => {
    if (typeof onNavigate === 'function') {
      e.preventDefault();
      scrollTop(0);
      onNavigate(page);
    }
  };

  return (
    <Container role="navigation" aria-label={ariaLabel || 'pagination'}>
      {prevEnabled ? (
        <Prev to={prevLink} onClick={(e) => onNav(e, pageIndex - 1)}>
          <ScreenReader>{t('accessibility-previous-page')}</ScreenReader>
          <ArrowWrapper>
            <SimpleArrow ariaHidden={true} />
          </ArrowWrapper>
        </Prev>
      ) : (
        <PrevDisabled />
      )}

      {items.map((item, index) => {
        if (item === null) {
          return <Gap key={`gap${index}`} aria-hidden={true} />;
        } else if (item === pageIndex) {
          return (
            <Current key={`current${item}`} aria-current={true}>
              <ScreenReader>{`${t('accessibility-current-page-prefix')} ${item}`}</ScreenReader>
              <span aria-hidden={true}>{item}</span>
            </Current>
          );
        } else {
          return (
            <Page
              to={buildPageLink({ baseUrl, page: item })}
              onClick={(e) => onNav(e, item)}
              key={`page${item}`}
            >
              <ScreenReader>{t('accessibility-page-prefix')} </ScreenReader>
              {item}
            </Page>
          );
        }
      })}
      {nextEnabled ? (
        <Next to={nextLink} onClick={(e) => onNav(e, pageIndex + 1)}>
          <ScreenReader>{t('accessibility-next-page')}</ScreenReader>
          <ArrowWrapper>
            <SimpleArrow ariaHidden={true} />
          </ArrowWrapper>
        </Next>
      ) : (
        <NextDisabled />
      )}
    </Container>
  );
};

export default Pagination;
